/**
 * CORE-AX — what a calendar day IS, in the shop's terms.
 *
 * The calendar used to mark "today" with the DEVICE's date (an owner checking the board from
 * another time zone saw the wrong day highlighted, and late in the evening every user was a day
 * off near midnight UTC-vs-local), and rendered a shop holiday exactly like a regular closed
 * weekday ("Closed"), so nobody could tell "we close Sundays" from "closed for July 4th".
 */
export interface ShopHoliday { date: string; name?: string | null }

/**
 * Core-100 R2 — CORE-AX-GAP-001: where the day sits in time. A FUTURE day's load is a projection
 * over what is booked now, not a forecast and not a fact, and must not render like today.
 */
export type CalendarTemporal = 'past' | 'today' | 'future';

export type CalendarDayStatus =
  | { kind: 'open'; isToday: boolean; temporal: CalendarTemporal }
  | { kind: 'closed'; isToday: boolean; temporal: CalendarTemporal }
  | { kind: 'holiday'; isToday: boolean; temporal: CalendarTemporal; label: string };

/** Shown on and for a forward day — the words a projection owes its reader. */
export const PROJECTION_NOTE = 'Projected from what is booked now — not a forecast; it will change as work is booked.';

/**
 * Core-100 R4 W5 — CORE-AX-GAP-002: a PAST day is not a record of that day. Its capacity is recomputed from TODAY's
 * roster (hours, efficiency, absences as they are now — roster history is not kept, CORE-W) and its load counts only
 * the work still open, so it is labelled as a reconstruction — never presented as what the day was.
 */
export const PAST_DAY_NOTE = "Recomputed from today's roster and the work still open — not a record of that day.";

/**
 * @param dayLabel YYYY-MM-DD of the calendar cell (a shop-local calendar day).
 * @param dayOfWeek 0=Sunday … 6=Saturday for that day.
 * @param shopTodayLabel YYYY-MM-DD "today" in the SHOP's time zone (getTodayInShopTz).
 */
export function calendarDayStatus(
  dayLabel: string,
  dayOfWeek: number,
  opts: { holidays: readonly ShopHoliday[]; openDays: ReadonlySet<number>; shopTodayLabel: string },
): CalendarDayStatus {
  const isToday = dayLabel === opts.shopTodayLabel;
  // YYYY-MM-DD labels order lexicographically as dates.
  const temporal: CalendarTemporal = isToday ? 'today' : dayLabel > opts.shopTodayLabel ? 'future' : 'past';
  const holiday = opts.holidays.find((h) => h?.date === dayLabel);
  if (holiday) {
    const name = typeof holiday.name === 'string' ? holiday.name.trim() : '';
    return { kind: 'holiday', isToday, temporal, label: name || 'Holiday' };
  }
  return opts.openDays.has(dayOfWeek) ? { kind: 'open', isToday, temporal } : { kind: 'closed', isToday, temporal };
}
